import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { UserRoleService } from '../user-services/user-roles.service';
import { UserRole } from '../../models/user-roles.class';
import { WorkOrderService } from '../../home/main/operation-reports/service-dept/services/work-order.service';
import { environment } from '../../../environments/environment';

export interface Queue {
  departmentId?: number;
  plant?: number;
  location?: number;
  userId?: string | number;
  assignedDeptId?: number[];
  statuses?: number[];
  notification?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class WorkOrderAlertService {
  private alertCountSubject = new BehaviorSubject<number>(0);

  alertCount$ = this.alertCountSubject.asObservable();

  private loaded = false;
  private loading = false;
  private inFlightPromise: Promise<void> | null = null;

  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;

  role: UserRole | undefined = undefined;

  constructor(
    private wos: WorkOrderService,
    private userRoleService: UserRoleService,
  ) {}

  async fetchAlertCount(role: UserRole, force = false): Promise<void> {
    if (!environment.staging) return;
    if (!role) {
      return;
    }
    // Don't fetch if we've already loaded this exact queue.
    if (!force && this.loaded) {
      return;
    }

    // Don't start another request while one is already running.
    if (this.inFlightPromise) {
      return this.inFlightPromise;
    }

    this.inFlightPromise = this.loadAlertCount(role);

    return this.inFlightPromise;
  }

  private async loadAlertCount(role: UserRole): Promise<void> {
    this.loading = true;

    try {
      this.role = role;

      if (!role) {
        this.alertCountSubject.next(0);
        this.loaded = true;
        return;
      }

      const total = await this.fetchAlertCountByCats();
      // const total = 0;
      // console.log('total: ', total);
      this.alertCountSubject.next(total);

      this.loaded = true;
    } catch (err) {
      console.error('Failed to fetch work order alert count:', err);
    } finally {
      this.loading = false;
      this.inFlightPromise = null;
    }
  }
  private async fetchAlertCountByCats(): Promise<number> {
    if (!environment.staging) {
      return 0;
    }

    const response = await this.fetchQueue();

    console.log('Response: ', response);
    return response?.total ?? 0;
  }

  async fetchQueue(): Promise<any> {
    const config = this.wos.handleRole();
    if (!config.role.queueCount) return;

    let response$;
    if (this.userRoleService.canAccessAnyActions(8, [5, 6])) {
      //service supervisors
      response$ = this.wos.fetchQueueFilter(
        undefined,
        undefined,
        undefined,
        undefined,
        config.role.queue?.assignedDeptId,
        config.notifications,
      );
    } else if (this.userRoleService.canAccessActions(8, [1, 2, 3, 4])) {
      //techs
      response$ = this.wos.fetchQueueFilter(
        undefined,
        undefined,
        undefined,
        config.role.queue?.userId,
        undefined,
        config.notifications,
      );
    } else {
      response$ = this.wos.fetchQueueFilter(
        config.deptIds,
        config.role?.plant,
        config.role?.location,
        undefined,
        undefined,
        config.notifications,
      );
    }
    return firstValueFrom(response$);
  }

  async fetchByCreator(): Promise<any> {
    const response$ = this.wos.fetchDataByCreator(
      this.userRoleService.getUser()?.name ?? '',
    );
    return firstValueFrom(response$);
  }
  fetchData() {
    const config = this.wos.handleRole();

    console.log(config?.approveIds);
    let response$;

    if (
      this.userRoleService.canAccessAnyActions(8, [5, 6]) ||
      this.userRoleService.canAccessActions(8, [1, 2, 3, 4])
    ) {
      // Service supervisors
      console.log(
        'Service Dept Fetch (' + config?.role.roleName + '): ',
        'Assigned Dept: ',
        config.role.queue?.assignedDeptId,
        'Statuses: ',
        config.statuses,
      );
      response$ = this.wos.fetchQueueFilter(
        undefined,
        undefined,
        undefined,
        undefined,
        config.role.queue?.assignedDeptId,
        config.statuses,
      );
    } else if (config.approveIds?.length > 0) {
      // Plant supervisors / approvers
      console.log(
        'Plant Supervisor Fetch(' + config?.role.roleName + '): ',
        'Dept: ',
        config.approveIds,
        'Plants: ',
        config.role?.plant,
        'Locations: ',
        config.role?.location,
        'Statuses: ',
        config.statuses,
      );

      response$ = this.wos.fetchQueueFilter(
        config.approveIds,
        config.role?.plant,
        config.role?.location,
        undefined,
        undefined,
        config.statuses,
      );
    } else {
      // Request creator
      console.log(
        'All Else Fetch(' + config?.role.roleName + '): ',
        'User: ',
        this.userRoleService.getUser()?.name,
      );
      response$ = this.wos.fetchDataByCreator(
        this.userRoleService.getUser()?.name ?? '',
      );
    }

    return firstValueFrom(response$);
  }
  updateRecord(add: boolean): void {
    const current = this.alertCountSubject.getValue();

    const next = add ? current + 1 : current - 1;

    this.alertCountSubject.next(Math.max(0, next));
  }

  get currentAlertCount(): number {
    return this.alertCountSubject.getValue();
  }

  refreshAlertCount(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    const role = this.userRoleService.getEffectiveRole();

    if (!role) {
      return;
    }

    this.refreshTimeout = setTimeout(() => {
      void this.fetchAlertCount(role);
    }, 300);
  }

  get isLoading(): boolean {
    return this.loading;
  }
}
